import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Radar, Users, Shield, Cpu, Zap, Star, ChevronDown,
  CheckCircle2, MessageCircle, LifeBuoy, Layers, TrendingUp,
  Globe, Building2, Award, User, ShieldCheck, Lock, FileCheck, Download, CheckCircle,
  FileSignature, GitBranch, Bell, Sparkles,
} from "lucide-react";
import MoonstoneWordmark from "../components/MoonstoneWordmark";

// ═════════════════════════════════════════════════════════════════════════
// i18n · semplice map IT/EN
// ═════════════════════════════════════════════════════════════════════════
const COPY = {
  // Nav & buttons
  nav_login:   { it: "Accedi",         en: "Log in" },
  nav_demo:    { it: "Prenota demo",   en: "Book a demo" },
  nav_free:    { it: "Prova gratis",   en: "Try for free" },
  // Hero
  hero_title:  {
    it: "Intelligence for Business",
    en: "Intelligence for Business",
  },
  hero_sub:    {
    it: "Moonstone Analytics trasforma dati grezzi, notizie e segnali di mercato in workflow di sales, supply chain e R&D intelligence pronti all'uso. Niente caos, solo chiarezza per i team commerciali, procurement e ricerca & sviluppo.",
    en: "Moonstone Analytics turns raw data, news and market signals into ready-to-use sales, supply chain and R&D intelligence workflows. No chaos, just clarity for your commercial, procurement and R&D teams.",
  },
  // Features
  features_kicker: { it: "Prodotto", en: "Product" },
  features_title:  {
    it: "Sales, Supply Chain, R&D & KYC Intelligence per team B2B moderni",
    en: "Sales, Supply Chain, R&D & KYC Intelligence for modern B2B teams",
  },
  // Testimonial
  testi_kicker: { it: "Testimonial", en: "Testimonial" },
  testi_quote: {
    it: "Con Moonstone abbiamo consolidato in 30 giorni tutta la nostra sales intelligence. Il team commerciale ora arriva alle call già preparato, con dati OSINT/SOCMINT e mappa delle relazioni pronte.",
    en: "With Moonstone we consolidated our entire sales intelligence in 30 days. Sales reps now walk into every call fully prepared, with OSINT/SOCMINT data and relationship maps ready.",
  },
  testi_author: { it: "Chief Revenue Officer, Innovex SRL", en: "Chief Revenue Officer, Innovex SRL" },
  testi_name:   { it: "Roberto Neri", en: "Roberto Neri" },
  // Metrics
  metrics_title: {
    it: "Le nostre soluzioni monitorano oltre 25M aziende e 150M contatti",
    en: "Our solutions monitor over 25M companies and 150M contacts",
  },
  metrics_sub: {
    it: "Supportiamo team commerciali, supply chain e R&D B2B con software di sales, competitive, supplier e technology intelligence affidabile e facile da usare. Moonstone semplifica War Room, KYC, ricerca profili e monitoraggio brevetti/tecnologie.",
    en: "We support B2B commercial, supply chain and R&D teams with reliable, user-friendly sales, competitive, supplier and technology intelligence software. Moonstone simplifies War Room, KYC, profile search and patents/tech monitoring.",
  },
  // Compliance & Regulations
  integr_kicker: { it: "Normativa", en: "Compliance" },
  integr_title: {
    it: "Sicurezza e conformità certificate",
    en: "Certified security and compliance",
  },
  integr_sub: {
    it: "Moonstone tratta i dati aziendali secondo i più alti standard internazionali. Piattaforma progettata per soddisfare i requisiti di privacy, sicurezza e controlli interni richiesti da settori regolati.",
    en: "Moonstone handles corporate data under the highest international standards. The platform is designed to meet privacy, security and internal-control requirements demanded by regulated industries.",
  },
  // FAQ
  faq_kicker: { it: "Support", en: "Support" },
  faq_title:  { it: "Domande frequenti", en: "FAQs" },
  faq_sub: {
    it: "Tutto quello che devi sapere sul prodotto e sui piani. Non trovi la risposta? Scrivi al team.",
    en: "Everything you need to know about the product and plans. Can't find the answer? Chat with the team.",
  },
  // Final CTA
  cta_title: {
    it: "Vuoi vedere Moonstone in azione? Parliamone",
    en: "Do you want to see Moonstone in action? Let's talk",
  },
  cta_sub: {
    it: "Unisciti alle aziende B2B che stanno già scalando la propria intelligence commerciale con Moonstone",
    en: "Join B2B companies already scaling their commercial intelligence with Moonstone",
  },
};
const t = (k, lang) => (COPY[k]?.[lang] ?? COPY[k]?.it ?? k);

// ═════════════════════════════════════════════════════════════════════════
// SCROLL REVEAL HOOK
// ═════════════════════════════════════════════════════════════════════════
function useInView({ threshold = 0.12 } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const RevealSection = ({ children, className = "", delay = 0, id }) => {
  const [ref, inView] = useInView();
  return (
    <section id={id} ref={ref} className={className}
      style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(24px)",
               transition: `opacity .8s ease ${delay}ms, transform .8s ease ${delay}ms` }}>
      {children}
    </section>
  );
};

// ═════════════════════════════════════════════════════════════════════════
// FAQ ACCORDION
// ═════════════════════════════════════════════════════════════════════════
function FaqItem({ q, a, open, onToggle, testid }) {
  return (
    <div>
      <button
        data-testid={testid}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-base font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div style={{
        maxHeight: open ? 400 : 0,
        opacity: open ? 1 : 0,
        transition: "max-height .35s ease, opacity .35s ease",
        overflow: "hidden",
      }}>
        <p className="text-sm text-slate-600 pb-5 leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════
export default function HomePage() {
  const [lang, setLang] = useState(() =>
    typeof window !== "undefined" ? (localStorage.getItem("moonstone_home_lang") || "it") : "it"
  );
  const setLangAndSave = (l) => { setLang(l); try { localStorage.setItem("moonstone_home_lang", l); } catch (err) { console.warn("[HomePage] persist lang failed:", err); } };
  const [openFaq, setOpenFaq] = useState(0);
  const T = (k) => t(k, lang);

  // ── Feature cards (Moonstone modules adapted from Formalize framework cards)
  const FEATURES = [
    {
      id: "kyc",
      title: "KYC · Account Check",
      icon: Shield, color: "#0EA5E9",
      screenshot: "/screenshots/kyc.jpg",
      desc_it: "Verifica in tempo reale. Ottieni dati anagrafici, fatturato, revenue, dipendenti e settore di ogni account con un click.",
      desc_en: "Real-time verification. Get registry data, revenue, employees and sector of any account with one click.",
      bullets_it: ["Verifica anagrafica istantanea", "Fatturato, revenue e settore", "Aggiungi ai profili con un click"],
      bullets_en: ["Instant registry verification", "Revenue, headcount and sector", "Add to profiles with one click"],
    },
    {
      id: "intelligence",
      title: "Intelligence for Business",
      icon: Users, color: "#A78BFA",
      screenshot: "/screenshots/intelligence.jpg",
      desc_it: "Search People, Search Company, People List e Business List con filtri massivi. Dati OSINT/SOCMINT/HUMINT ricchi per ogni profilo.",
      desc_en: "Search People, Search Company, People List and Business List with massive filters. Rich OSINT/SOCMINT/HUMINT data on every profile.",
      bullets_it: ["Ricerca contatti e aziende", "Setup KYC guidato", "Visibilità totale sui rischi"],
      bullets_en: ["People & company search", "Guided KYC setup", "Complete risk visibility"],
    },
    {
      id: "events",
      title: "Events and Fairs",
      icon: Award, color: "#059669",
      desc_it: "B2B Fairs e B2B Events uniti in un unico monitor. Mappa cliccabile, timeline azienda, export CSV/PDF partecipanti. Non perderti più un evento chiave.",
      desc_en: "B2B Fairs and B2B Events merged in a single monitor. Clickable map, company timeline, CSV/PDF attendee export. Never miss a key event again.",
      bullets_it: ["Registro eventi partecipanti", "Automazione audit fornitori", "Data Subject Requests"],
      bullets_en: ["Attendee register", "Supplier audit automation", "Data Subject Requests"],
    },
    {
      id: "warroom",
      title: "War Room",
      icon: Radar, color: "#00E5FF",
      screenshot: "/screenshots/war-room.jpg",
      desc_it: "Mappa geografica live di clienti, fornitori, prospect e competitor. Rating semaforico e alert automatici su fornitori in peggioramento.",
      desc_en: "Live geographic map of clients, suppliers, prospects and competitors. Traffic-light rating and automatic alerts on declining suppliers.",
      bullets_it: ["Rating semaforico live", "Setup guidato completo", "Copertura rischio end-to-end"],
      bullets_en: ["Live traffic-light rating", "Guided full setup", "End-to-end risk oversight"],
    },
    {
      id: "rd",
      title: "Business Asset",
      icon: Cpu, color: "#F59E0B",
      desc_it: "Vista aziendale unificata: brevetti, tecnologie, macchine CNC, macchinari, flotta veicoli. Ottieni un quadro completo dell'infrastruttura tecnica di ogni target.",
      desc_en: "Unified company view: patents, technologies, CNC machines, machinery, vehicle fleet. Get a complete picture of any target's technical infrastructure.",
      bullets_it: ["Documentazione centralizzata", "Multi-framework in scioltezza", "Tracking rischio & incidenti"],
      bullets_en: ["Centralized documentation", "Multiple frameworks easily", "Risk & incident tracking"],
    },
    {
      id: "contract-twin",
      title: "Contract Twin",
      icon: FileSignature, color: "#B39BFF",
      desc_it: "Digital twin dei contratti B2B: estrazione clausole via AI, alert su tasso BCE / valute / commodity, grafo delle dipendenze e simulazioni what-if per anticipare rinegoziazioni.",
      desc_en: "Digital twin of B2B contracts: AI clause extraction, alerts on ECB rate / FX / commodities, dependency graph and what-if simulations to anticipate renegotiations.",
      bullets_it: ["Estrazione clausole con AI", "Alert tasso BCE e variabili di mercato", "Timeline collaborativa con owner"],
      bullets_en: ["Clause extraction with AI", "ECB rate and market variable alerts", "Collaborative timeline with owner"],
    },
    {
      id: "custom",
      title: lang === "it" ? "Custom" : "Custom",
      icon: Layers, color: "#7C3AED",
      desc_it: "Configura il tuo programma di intelligence su misura. Moonstone si adatta a controlli, workflow e documentazione specifici del tuo settore.",
      desc_en: "Configure your intelligence program on-demand. Moonstone adapts to your industry-specific controls, workflows and documentation.",
      bullets_it: ["Personalizzabile alle tue esigenze", "Richiedi framework dedicati", "Feature request rapide"],
      bullets_en: ["Customizable to your needs", "Request your own frameworks", "Fast feature requests"],
    },
  ];

  const METRICS = [
    { value: "25M+",  label_it: "Aziende monitorate",   label_en: "Companies monitored" },
    { value: "150M+", label_it: "Contatti profilati",    label_en: "Profiles tracked" },
    { value: "60+",   label_it: "Paesi coperti",         label_en: "Countries covered" },
    { value: "4+",    label_it: "Lingue supportate",     label_en: "Languages supported" },
  ];

  const COMPLIANCE = [
    {
      id: "gdpr",
      name: "GDPR",
      spec: "Reg. UE 2016/679",
      title_it: "Privacy dei dati personali",
      title_en: "Personal data privacy",
      color: "#0033A0",
      icon: Shield,
      controls_it: ["Privacy by design", "Notifica breach 72h", "Portabilità dati", "Registro dei trattamenti"],
      controls_en: ["Privacy by design", "72h breach notification", "Data portability", "Processing records"],
      status_it: "Conforme",
      status_en: "Compliant",
    },
    {
      id: "iso",
      name: "ISO 27001",
      spec: "ISO/IEC 27001:2022",
      title_it: "Information Security Management",
      title_en: "Information Security Management",
      color: "#0891B2",
      icon: ShieldCheck,
      controls_it: ["Risk assessment", "114 controlli Annex A", "Business Continuity", "Audit annuali"],
      controls_en: ["Risk assessment", "114 Annex A controls", "Business Continuity", "Annual audits"],
      status_it: "Certificato",
      status_en: "Certified",
    },
    {
      id: "soc2",
      name: "SOC 2",
      spec: "Type II · TSC 2017",
      title_it: "Sicurezza & affidabilità",
      title_en: "Security & reliability",
      color: "#16A34A",
      icon: Lock,
      controls_it: ["Security", "Availability", "Confidentiality", "Processing integrity"],
      controls_en: ["Security", "Availability", "Confidentiality", "Processing integrity"],
      status_it: "Audit indipendente",
      status_en: "Independent audit",
    },
  ];

  const SECURITY_HIGHLIGHTS = lang === "it" ? [
    { icon: Globe,     label: "Data residency in UE",          sub: "Server AWS / Azure Frankfurt & Milano" },
    { icon: Lock,      label: "Crittografia AES-256",           sub: "In transito (TLS 1.3) e a riposo" },
    { icon: FileCheck, label: "Log immutabili & audit trail",   sub: "Tracciabilità completa di ogni azione" },
    { icon: Users,     label: "RBAC & SSO enterprise",          sub: "SAML 2.0, OIDC, MFA obbligatorio" },
  ] : [
    { icon: Globe,     label: "EU data residency",              sub: "AWS / Azure Frankfurt & Milan" },
    { icon: Lock,      label: "AES-256 encryption",             sub: "In transit (TLS 1.3) and at rest" },
    { icon: FileCheck, label: "Immutable logs & audit trail",   sub: "Full traceability of every action" },
    { icon: Users,     label: "Enterprise RBAC & SSO",          sub: "SAML 2.0, OIDC, mandatory MFA" },
  ];

  const FAQS = lang === "it" ? [
    { q: "È disponibile una prova gratuita?",
      a: "Sì! Puoi provare Moonstone gratuitamente per 14 giorni senza inserire alcuna carta di credito. Clicca su \"Prova gratis\" o prenota una demo con il team." },
    { q: "Perché le aziende scelgono Moonstone rispetto ai tool tradizionali?",
      a: "Le aziende scelgono Moonstone quando vogliono superare fogli Excel e sistemi frammentati. Moonstone offre una piattaforma unificata dove sales intelligence, competitive intelligence, KYC e monitoraggio fornitori sono connessi in un unico posto. Il risultato: meno lavoro manuale, più visibilità e velocità decisionale." },
    { q: "Quanti utenti sono inclusi?",
      a: "Puoi aggiungere tutti gli utenti che ti servono. Puoi invitare anche persone esterne, come consulenti o partner. Una volta invitati, avranno accesso autorizzato al sistema secondo i ruoli configurati." },
    { q: "Che tipo di supporto offrite durante l'implementazione?",
      a: "Il nostro team di customer success ti affianca in tutto il processo di onboarding. Verrai schedulato per un incontro dedicato dove rispondiamo a tutte le domande. La piattaforma è comunque intuitiva e facile da usare anche in autonomia." },
    { q: "Quali fonti dati e integrazioni supportate?",
      a: "Moonstone integra fonti di people search, provider KYC per compliance e verifiche antifrede, fonti SOCMINT, oltre ai principali CRM di mercato e suite di produttività aziendale. Il sistema espone anche un'API completa per integrazioni custom." },
    { q: "Chi utilizza tipicamente Moonstone?",
      a: "Moonstone è usato da team commerciali, business development, procurement, competitive intelligence, KYC/compliance e M&A advisor. È particolarmente indicato per aziende B2B che devono coordinare sales intelligence su portafogli grandi e settori regolati." },
  ] : [
    { q: "Is there a free trial available?",
      a: "Yes! You can try Moonstone for free for 14 days without providing any credit card. Click \"Try for free\" or book a demo with the team." },
    { q: "Why do organizations choose Moonstone over traditional tools?",
      a: "Companies choose Moonstone when they want to move beyond spreadsheets and fragmented systems. Moonstone provides a unified platform where sales intelligence, competitive intelligence, KYC and supplier monitoring are connected in one place. The result: less manual work, more visibility, faster decisions." },
    { q: "How many users are included?",
      a: "You can add as many users as you need. You can invite external people too, consultants or partners. Once invited, they gain role-based access to the platform." },
    { q: "What support do you provide during implementation?",
      a: "Our customer success team supports you through the onboarding process. You will be scheduled for a dedicated kickoff meeting to answer all your questions. The platform is intuitive and easy to use also without our help." },
    { q: "Which data sources and integrations do you support?",
      a: "Moonstone integrates people search sources, KYC providers for compliance and anti-fraud checks, SOCMINT sources, plus leading CRMs and productivity suites. A full API is also available for custom integrations." },
    { q: "Who typically uses Moonstone?",
      a: "Moonstone is used by sales teams, business development, procurement, competitive intelligence, KYC/compliance and M&A advisors. Especially valuable for B2B companies managing sales intelligence on large portfolios and regulated sectors." },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* ═══════════════════════════════════════════════════════════════════
          Top nav — floating dark pill (stile Omoda&Jaecoo)
      ═══════════════════════════════════════════════════════════════════ */}
      <header className="sticky top-4 z-40 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="relative flex items-center h-14 sm:h-16 rounded-2xl px-4 sm:px-6 border border-white/[0.03] backdrop-blur-xl overflow-hidden"
               style={{
                 background: "linear-gradient(120deg, rgba(1,3,15,0.96) 0%, rgba(3,7,28,0.95) 40%, rgba(6,12,42,0.94) 75%, rgba(9,18,58,0.94) 100%)",
                 boxShadow: "0 12px 40px -14px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.55)"
               }}>
            {/* Inner glow accents (blu profondo con highlight ciano molto tenue) */}
            <div aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl"
                 style={{ background: "radial-gradient(120% 80% at 10% 0%, rgba(56,189,248,0.06) 0%, transparent 45%), radial-gradient(140% 100% at 100% 100%, rgba(15,32,90,0.35) 0%, transparent 55%)" }} />

            {/* LEFT · logo + menu */}
            <div className="relative flex items-center gap-10 lg:gap-16">
              <Link to="/" data-testid="home-logo-link" className="flex items-center shrink-0">
                <img
                  src="/branding/moonstone-analytics.png"
                  alt="Moonstone Analytics"
                  draggable={false}
                  style={{ height: 80, width: "auto", display: "block" }}
                />
              </Link>
              <nav className="hidden md:flex items-center gap-6 lg:gap-8 text-[13px] font-semibold text-white/85 tracking-wide">
                <a href="#features"     className="hover:text-white transition-colors">{lang === "it" ? "Prodotto" : "Product"}</a>
                <a href="#metrics"      className="hover:text-white transition-colors">{lang === "it" ? "Clienti" : "Customers"}</a>
                <a href="#compliance"   className="hover:text-white transition-colors">{lang === "it" ? "Normativa" : "Compliance"}</a>
                <a href="#faq"          className="hover:text-white transition-colors">FAQ</a>
              </nav>
            </div>

            {/* RIGHT · lang + auth */}
            <div className="relative ml-auto flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:flex items-center rounded-full bg-white/5 border border-white/5 overflow-hidden text-[11px] font-semibold">
                <button data-testid="lang-it" onClick={() => setLangAndSave("it")}
                  className={`px-2.5 py-1 transition-colors ${lang === "it" ? "bg-white text-slate-900" : "text-white/70 hover:text-white"}`}>IT</button>
                <button data-testid="lang-en" onClick={() => setLangAndSave("en")}
                  className={`px-2.5 py-1 transition-colors ${lang === "en" ? "bg-white text-slate-900" : "text-white/70 hover:text-white"}`}>EN</button>
              </div>
              <Link to="/login" data-testid="home-login-link"
                className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/85 hover:text-white px-3 py-2 transition-colors">
                <User className="w-4 h-4" />
                {T("nav_login")}
              </Link>
              <Link to="/login" data-testid="home-cta-topbar"
                className="inline-flex items-center gap-1 bg-white text-slate-900 text-[13px] font-semibold px-4 sm:px-5 py-2 rounded-full hover:bg-slate-100 transition-colors">
                {T("nav_demo")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════════════════════════════ */}
      <RevealSection className="relative overflow-hidden">
        {/* soft decorative gradient blobs */}
        <div aria-hidden className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 60%)" }} />
        <div aria-hidden className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #A78BFA 0%, transparent 60%)" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#0891B2] bg-[#00E5FF]/10 border border-[#00E5FF]/40 px-3 py-1.5 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
              {lang === "it" ? "Nuova release · Timeline B2B Events" : "New release · B2B Events Timeline"}
            </div>
            <h1 data-testid="home-hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              {T("hero_title")}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              {T("hero_sub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" data-testid="home-cta-primary"
                className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-5 py-3 rounded-full hover:bg-slate-800 transition-colors">
                {T("nav_demo")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="home-cta-secondary"
                className="inline-flex items-center gap-2 bg-white border border-slate-100 text-slate-900 text-sm font-semibold px-5 py-3 rounded-full hover:bg-slate-50 transition-colors">
                {T("nav_free")}
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-3 text-xs text-slate-500">
              <div className="flex items-center gap-0.5 text-[#F59E0B]">
                {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5" fill="currentColor" />)}
              </div>
              <span className="font-semibold text-slate-700">4.9 / 5.0</span>
              <span>{lang === "it" ? "· usato da team commerciali B2B in tutta Europa" : "· used by B2B sales teams across Europe"}</span>
            </div>
          </div>

          {/* Product mockup */}
          <div className="relative">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-2 shadow-2xl">
              <div className="bg-slate-950 rounded-2xl p-4">
                <div className="flex items-center gap-1.5 mb-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {[
                    { l: "War Room · Live",   v: "247 marker",   c: "#00E5FF", i: Radar },
                    { l: "Intelligence",       v: "1.2K contatti", c: "#A78BFA", i: Users },
                    { l: "KYC · Verifiche",    v: "89 attive",     c: "#059669", i: Shield },
                    { l: "Business Asset",     v: "211 aziende",   c: "#F59E0B", i: Cpu },
                  ].map((k) => (
                    <div key={k.l} className="bg-slate-900 border border-slate-900 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] tracking-widest uppercase text-slate-400">{k.l}</p>
                        <k.i className="w-3 h-3" style={{ color: k.c }} />
                      </div>
                      <p className="text-lg font-bold" style={{ color: k.c }}>{k.v}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 bg-slate-900 border border-slate-900 rounded-lg p-3">
                  <p className="text-[10px] tracking-widest uppercase text-slate-400 mb-2 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-[#16C784]" /> Trend fornitori · ultimi 90gg
                  </p>
                  <div className="flex items-end gap-1 h-16">
                    {[42, 55, 38, 62, 71, 48, 76, 83, 68, 90, 78, 95].map((h, i) => (
                      <div key={`bar-${i}-${h}`} className="flex-1 rounded-t"
                        style={{ height: `${h}%`, background: `linear-gradient(180deg, #00E5FF, #0891B2)`, opacity: 0.4 + i * 0.05 }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {/* Floating cards */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl border border-slate-100/60 p-3 flex items-center gap-2 max-w-[200px]">
              <CheckCircle2 className="w-5 h-5 text-[#16C784] flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-900">KYC approvato</p>
                <p className="text-[10px] text-slate-500">Innovex SRL · 2 min fa</p>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl border border-slate-100/60 p-3 flex items-center gap-2 max-w-[220px]">
              <div className="w-8 h-8 rounded-full bg-[#A78BFA]/15 flex items-center justify-center">
                <Zap className="w-4 h-4 text-[#A78BFA]" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-900">Nuovo insight</p>
                <p className="text-[10px] text-slate-500">3 fornitori in miglioramento</p>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* ═══════════════════════════════════════════════════════════════════
          FEATURE CARDS
      ═══════════════════════════════════════════════════════════════════ */}
      <RevealSection id="features" className="bg-slate-50 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest uppercase text-[#0891B2] mb-3">{T("features_kicker")}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight max-w-3xl">
            {T("features_title")}
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">
            {lang === "it"
              ? "Costruisci l'intelligence commerciale che si adatta a te: i tuoi settori, i tuoi workflow, il tuo ritmo. Il team ti supporta ad ogni step."
              : "Build a commercial intelligence setup that fits you: your sectors, your workflows, your pace. Our team supports you every step."}
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.id}
                data-testid={`home-feature-${f.id}`}
                className="bg-white border border-slate-100/60 rounded-2xl p-6 hover:border-slate-100 hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${f.color}15`, border: `1px solid ${f.color}44` }}>
                  <f.icon className="w-6 h-6" style={{ color: f.color }} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  {lang === "it" ? f.desc_it : f.desc_en}
                </p>
                <ul className="space-y-1.5 mb-5">
                  {(lang === "it" ? f.bullets_it : f.bullets_en).map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: f.color }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/login" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 group-hover:text-slate-700">
                  {lang === "it" ? "Scopri di più" : "Learn more"} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ═══════════════════════════════════════════════════════════════════
          CONTRACT TWIN — dedicated deep-dive section
      ═══════════════════════════════════════════════════════════════════ */}
      <RevealSection id="contract-twin" className="bg-gradient-to-br from-slate-950 via-[#0A0E1A] to-slate-950 text-white py-20 sm:py-28 overflow-hidden relative">
        <div aria-hidden className="absolute inset-0 opacity-25">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #B39BFF 0%, transparent 60%)" }} />
          <div className="absolute bottom-10 left-10 w-80 h-80 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 60%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            {/* LEFT — copy */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#B39BFF]/12 border border-[#B39BFF]/30 rounded-full px-3 py-1 mb-5">
                <FileSignature className="w-3.5 h-3.5 text-[#B39BFF]" />
                <span className="text-[11px] font-bold tracking-widest uppercase text-[#B39BFF]">
                  {lang === "it" ? "Nuovo · Contract Twin" : "New · Contract Twin"}
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                {lang === "it" ? (
                  <>Il gemello digitale dei tuoi contratti B2B <span className="text-[#B39BFF]">che anticipa le rinegoziazioni</span>.</>
                ) : (
                  <>The digital twin of your B2B contracts <span className="text-[#B39BFF]">that anticipates renegotiations</span>.</>
                )}
              </h2>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-xl">
                {lang === "it"
                  ? "Carica i PDF, l'AI estrae clausole rinnovo, revisione prezzo, penali e recesso. Ti avvisa quando il tasso BCE, l'EUR/USD o il Brent superano le soglie che innescano automatismi contrattuali."
                  : "Upload PDFs, AI extracts renewal, price revision, penalty and termination clauses. Get notified when the ECB rate, EUR/USD or Brent cross thresholds that trigger contractual automations."}
              </p>

              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Sparkles, color: "#B39BFF",
                    title_it: "Estrazione AI multi-tipo",
                    title_en: "Multi-type AI extraction",
                    body_it: "5 template specializzati (Leasing, Fornitura indicizzata, SaaS, NDA, Altro) con prompt AI ottimizzati per tipo di contratto.",
                    body_en: "5 specialized templates (Leasing, Indexed Supply, SaaS, NDA, Other) with AI prompts optimized per contract type.",
                  },
                  {
                    icon: Bell, color: "#FFB800",
                    title_it: "Alert su variabili esterne",
                    title_en: "External variable alerts",
                    body_it: "Monitor live di Tasso BCE, EUR/USD, Brent. Ogni volta che una soglia contrattuale è superata ricevi l'esposizione economica aggregata dei contratti impattati.",
                    body_en: "Live monitor of ECB rate, EUR/USD, Brent. Every time a contractual threshold is crossed you get the aggregate economic exposure of impacted contracts.",
                  },
                  {
                    icon: GitBranch, color: "#00E5FF",
                    title_it: "Grafo delle dipendenze",
                    title_en: "Dependency graph",
                    body_it: "Traversal BFS multi-hop delle clausole di parità: se rinegozi un contratto master, sai in un click quali contratti collegati subiscono l'impatto a cascata.",
                    body_en: "Multi-hop BFS traversal of parity clauses: if you renegotiate a master contract, you know in one click which linked contracts cascade the impact.",
                  },
                  {
                    icon: Users, color: "#00E676",
                    title_it: "Timeline collaborativa",
                    title_en: "Collaborative timeline",
                    body_it: "Assegna owner, stato e note direttamente sulle finestre di rinegoziazione. Il team commerciale sa cosa muovere prima che le clausole scattino.",
                    body_en: "Assign owner, status and notes directly on renegotiation windows. Sales knows what to move before clauses trigger.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4" data-testid={`home-ct-feature-${i}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${item.color}18`, border: `1px solid ${item.color}55` }}>
                      <item.icon className="w-5 h-5" style={{ color: item.color }} />
                    </div>
                    <div>
                      <p className="text-base font-bold text-white">{lang === "it" ? item.title_it : item.title_en}</p>
                      <p className="text-sm text-slate-400 leading-relaxed mt-0.5">{lang === "it" ? item.body_it : item.body_en}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/login" data-testid="home-ct-cta-primary"
                  className="inline-flex items-center gap-2 bg-[#B39BFF] text-slate-950 text-sm font-bold px-5 py-3 rounded-full hover:bg-white transition-colors">
                  {lang === "it" ? "Prova Contract Twin" : "Try Contract Twin"} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/login" data-testid="home-ct-cta-secondary"
                  className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white text-sm font-semibold px-5 py-3 rounded-full hover:bg-white/10 transition-colors">
                  {lang === "it" ? "Richiedi una demo" : "Book a demo"}
                </Link>
              </div>
            </div>

            {/* RIGHT — visual mock (composed clause card + alert) */}
            <div className="relative">
              <div className="bg-[#0D0F14] border border-white/[0.06] rounded-2xl p-5 shadow-2xl" data-testid="home-ct-mock-detail">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileSignature className="w-4 h-4 text-[#B39BFF]" />
                    <span className="text-[10px] font-bold tracking-widest uppercase text-[#B39BFF]">
                      {lang === "it" ? "master_contract.pdf" : "master_contract.pdf"}
                    </span>
                  </div>
                  <span className="text-[9px] px-2 py-0.5 bg-[#00E676]/12 text-[#00E676] font-bold uppercase tracking-widest rounded">
                    {lang === "it" ? "Elaborato" : "Processed"}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-400 mb-4">
                  <span>{lang === "it" ? "Tipo" : "Type"}: <span className="text-[#B39BFF] font-bold">Leasing</span></span>
                  <span>{lang === "it" ? "Valore" : "Value"}: <span className="text-white font-bold tabular-nums">€1.200.000</span></span>
                  <span>{lang === "it" ? "Controparte" : "Counterparty"}: <span className="text-white">Beta S.r.l.</span></span>
                </div>

                {/* clause row */}
                <div className="border-l-4 border-[#FFB800] bg-white/[0.02] px-3 py-2 mb-2 rounded-r">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-white">{lang === "it" ? "Revisione prezzo" : "Price revision"}</span>
                    <span className="text-[10px] text-[#A0AEC0]">Trigger: <span className="text-white">{lang === "it" ? "Tasso BCE" : "ECB rate"}</span> ≥ 4.5%</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {lang === "it" ? "Adeguamento canone del +8% se soglia superata per 3 mesi." : "Fee adjustment of +8% if threshold exceeded for 3 months."}
                  </p>
                </div>
                <div className="border-l-4 border-[#00E5FF] bg-white/[0.02] px-3 py-2 mb-2 rounded-r">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-white">{lang === "it" ? "Rinnovo" : "Renewal"}</span>
                    <span className="text-[10px] text-[#A0AEC0]">{lang === "it" ? "Scadenza" : "Deadline"}: 2027-03-15</span>
                  </div>
                </div>
              </div>

              {/* Floating alert card */}
              <div className="absolute -bottom-6 -right-4 sm:-right-8 w-72 bg-white text-slate-900 rounded-xl p-4 shadow-2xl border border-slate-100" data-testid="home-ct-mock-alert">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#FF1744]/15 flex items-center justify-center flex-shrink-0">
                    <Bell className="w-4 h-4 text-[#FF1744]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-900">
                      {lang === "it" ? "Alert Tasso BCE" : "ECB rate alert"}
                    </p>
                    <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                      {lang === "it"
                        ? "Soglia 4.5% superata · 3 contratti impattati · esposizione €2.4M"
                        : "4.5% threshold crossed · 3 contracts impacted · €2.4M exposure"}
                    </p>
                    <p className="text-[10px] text-[#00E676] font-bold uppercase tracking-widest mt-2">
                      {lang === "it" ? "Rinegoziare entro 45gg" : "Renegotiate within 45 days"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* ═══════════════════════════════════════════════════════════════════
          TESTIMONIAL — hidden per user request (kept for future re-enable)
      ═══════════════════════════════════════════════════════════════════ */}
      {false && (
      <RevealSection className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold tracking-widest uppercase text-[#0891B2] mb-4">{T("testi_kicker")}</p>
          <blockquote className="text-2xl sm:text-3xl lg:text-4xl font-medium text-slate-900 leading-tight">
            &ldquo;{T("testi_quote")}&rdquo;
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-semibold">
              RN
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-900">{T("testi_name")}</p>
              <p className="text-xs text-slate-600">{T("testi_author")}</p>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/login" className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-5 py-3 rounded-full hover:bg-slate-800 transition-colors">
              {T("nav_demo")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="inline-flex items-center gap-2 bg-white border border-slate-100 text-slate-900 text-sm font-semibold px-5 py-3 rounded-full hover:bg-slate-50 transition-colors">
              {T("nav_free")}
            </Link>
          </div>
        </div>
      </RevealSection>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          METRICS
      ═══════════════════════════════════════════════════════════════════ */}
      <RevealSection id="metrics" className="relative bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white py-20 sm:py-28 overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-80 h-80 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 60%)" }} />
          <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #A78BFA 0%, transparent 60%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              {T("metrics_title")}
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              {T("metrics_sub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" className="inline-flex items-center gap-2 bg-white text-slate-900 text-sm font-semibold px-5 py-3 rounded-full hover:bg-slate-100 transition-colors">
                {T("nav_demo")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 border border-white/10 text-white text-sm font-semibold px-5 py-3 rounded-full hover:bg-white/10 transition-colors">
                {T("nav_free")}
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {METRICS.map((m, i) => (
              <div key={i} className="bg-white/5 backdrop-blur border border-white/5 rounded-2xl p-6">
                <p className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{m.value}</p>
                <p className="mt-2 text-xs tracking-widest uppercase text-slate-300">
                  {lang === "it" ? m.label_it : m.label_en}
                </p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ═══════════════════════════════════════════════════════════════════
          COMPLIANCE / NORMATIVA
      ═══════════════════════════════════════════════════════════════════ */}
      <RevealSection id="compliance" className="py-20 sm:py-28 relative overflow-hidden">
        {/* subtle background pattern */}
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.35]"
             style={{ background: "radial-gradient(90% 60% at 50% 0%, #EFF6FF 0%, transparent 60%)" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-px bg-[#0891B2]" />
              <p className="text-xs font-bold tracking-widest uppercase text-[#0891B2]">{T("integr_kicker")}</p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              {T("integr_title")}
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              {T("integr_sub")}
            </p>
          </div>

          {/* Certification cards */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {COMPLIANCE.map((c) => {
              const CIcon = c.icon;
              const controls = lang === "it" ? c.controls_it : c.controls_en;
              const title = lang === "it" ? c.title_it : c.title_en;
              const status = lang === "it" ? c.status_it : c.status_en;
              return (
                <div
                  key={c.id}
                  data-testid={`compliance-${c.id}`}
                  className="group relative bg-white border border-slate-100/60 rounded-2xl p-6 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                >
                  {/* color accent top bar */}
                  <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ backgroundColor: c.color }} />

                  {/* Header: seal + status */}
                  <div className="flex items-start justify-between gap-3">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 relative"
                      style={{ backgroundColor: `${c.color}12` }}
                    >
                      <CIcon className="w-7 h-7" style={{ color: c.color }} strokeWidth={2} />
                    </div>
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                      style={{ color: c.color, backgroundColor: `${c.color}12` }}
                    >
                      <CheckCircle className="w-3 h-3" strokeWidth={2.5} />
                      {status}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="mt-5">
                    <p className="text-2xl font-bold text-slate-900 tracking-tight leading-none">{c.name}</p>
                    <p className="text-[10px] font-mono font-semibold uppercase tracking-widest text-slate-400 mt-1.5">{c.spec}</p>
                    <p className="mt-3 text-sm font-semibold text-slate-700">{title}</p>
                  </div>

                  {/* Controls list */}
                  <ul className="mt-4 space-y-1.5 pt-4">
                    {controls.map((ctl) => (
                      <li key={ctl} className="flex items-center gap-2 text-[13px] text-slate-600">
                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: c.color }} strokeWidth={2.5} />
                        <span>{ctl}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Security highlights strip */}
          <div className="mt-12 bg-slate-900 rounded-2xl p-6 sm:p-8 overflow-hidden relative">
            <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none"
                 style={{ background: "radial-gradient(60% 90% at 100% 0%, rgba(8,145,178,0.35) 0%, transparent 55%)" }} />
            <div className="relative flex items-center gap-3 mb-6">
              <ShieldCheck className="w-5 h-5 text-[#0891B2]" strokeWidth={2} />
              <p className="text-xs font-bold uppercase tracking-widest text-white/70">
                {lang === "it" ? "Cosa proteggiamo per te" : "What we protect for you"}
              </p>
            </div>
            <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {SECURITY_HIGHLIGHTS.map((h) => {
                const HIcon = h.icon;
                return (
                  <div key={h.label} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                      <HIcon className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white leading-tight">{h.label}</p>
                      <p className="text-xs text-white/60 leading-snug mt-0.5">{h.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Trust bar + CTA */}
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              <span className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-slate-400" />{lang === "it" ? "Ultimo audit: Q4 2025" : "Latest audit: Q4 2025"}</span>
              <span className="flex items-center gap-1.5"><FileCheck className="w-3.5 h-3.5 text-slate-400" />{lang === "it" ? "Rinnovato annualmente" : "Renewed annually"}</span>
              <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-slate-400" />NIS2 · DORA {lang === "it" ? "ready" : "ready"}</span>
            </div>
            <a
              href="mailto:security@moonstoneanalytics.com?subject=Security%20Whitepaper%20Request"
              data-testid="download-security-whitepaper"
              className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-5 py-3 rounded-full hover:bg-slate-800 transition-colors group"
            >
              <Download className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
              {lang === "it" ? "Richiedi il Security Whitepaper" : "Request the Security Whitepaper"}
            </a>
          </div>
        </div>
      </RevealSection>

      {/* ═══════════════════════════════════════════════════════════════════
          FAQ
      ═══════════════════════════════════════════════════════════════════ */}
      <RevealSection id="faq" className="bg-slate-50 py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest uppercase text-[#0891B2] mb-3">{T("faq_kicker")}</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{T("faq_title")}</h2>
          <p className="mt-3 text-base text-slate-600">{T("faq_sub")}</p>
          <div className="mt-10 bg-white rounded-2xl px-6 divide-y divide-slate-100">
            {FAQS.map((f, i) => (
              <FaqItem
                key={i}
                q={f.q} a={f.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                testid={`home-faq-item-${i}`}
              />
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ═══════════════════════════════════════════════════════════════════
          FINAL CTA
      ═══════════════════════════════════════════════════════════════════ */}
      <RevealSection className="relative py-20 sm:py-28 overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 60%)" }} />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            {T("cta_title")}
          </h2>
          <p className="mt-4 text-lg text-slate-600">{T("cta_sub")}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/login" data-testid="home-cta-final-demo"
              className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-slate-800 transition-colors">
              {T("nav_demo")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" data-testid="home-cta-final-free"
              className="inline-flex items-center gap-2 bg-white border border-slate-100 text-slate-900 text-sm font-semibold px-6 py-3 rounded-full hover:bg-slate-50 transition-colors">
              {T("nav_free")}
            </Link>
          </div>
        </div>
      </RevealSection>

      {/* ═══════════════════════════════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════════════════════════════ */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            <div>
              <MoonstoneWordmark size="sm" variant="monogram" />
              <p className="mt-4 text-xs leading-relaxed">
                {lang === "it"
                  ? "Moonstone Analytics · Intelligence for Business per team commerciali B2B."
                  : "Moonstone Analytics · Intelligence for Business for B2B sales teams."}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-white tracking-widest uppercase mb-3">{lang === "it" ? "Prodotto" : "Product"}</p>
              <ul className="space-y-2 text-xs">
                <li><a href="#features" className="hover:text-white">War Room</a></li>
                <li><a href="#features" className="hover:text-white">Intelligence</a></li>
                <li><a href="#features" className="hover:text-white">KYC</a></li>
                <li><a href="#features" className="hover:text-white">R&amp;D</a></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-white tracking-widest uppercase mb-3">{lang === "it" ? "Azienda" : "Company"}</p>
              <ul className="space-y-2 text-xs">
                <li><a href="#metrics" className="hover:text-white">{lang === "it" ? "Clienti" : "Customers"}</a></li>
                <li><a href="#compliance" className="hover:text-white">{lang === "it" ? "Normativa" : "Compliance"}</a></li>
                <li><a href="#faq" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-white tracking-widest uppercase mb-3">{lang === "it" ? "Contatti" : "Contact"}</p>
              <ul className="space-y-2 text-xs">
                <li><Link to="/login" className="hover:text-white flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {T("nav_demo")}</Link></li>
                <li><Link to="/login" className="hover:text-white flex items-center gap-1"><LifeBuoy className="w-3 h-3" /> {lang === "it" ? "Support" : "Support"}</Link></li>
                <li><a href="mailto:info@moonstoneanalytics.com" className="hover:text-white flex items-center gap-1"><Globe className="w-3 h-3" /> info@moonstoneanalytics.com</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 flex flex-col sm:flex-row flex-wrap justify-between items-start sm:items-center gap-3 text-[10px] text-slate-500 tracking-wider">
            <p className="uppercase tracking-widest">Moonstone Analytics V2.89 | Intelligence for Business</p>
            <p className="flex items-start gap-1.5 leading-relaxed max-w-full sm:max-w-[70%] sm:text-right normal-case">
              <Building2 className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>© {new Date().getFullYear()} QARYA SRL | VAT IT13280220966 | Salizada San Canzian 5557 Venezia VE Italy | Share Cap. € 10.000 | REA No. VE468630. All rights reserved.</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
